import { type FormEvent, useEffect, useState } from 'react'
import { Github, Gitlab, Loader2, Plus, Search, X } from 'lucide-react'
import GitHubRepoBrowser from '../GitHubRepoBrowser'
import GitLabRepoBrowser from '../GitLabRepoBrowser'
import {
  getGitHubLinkStatus,
  getGitHubLinkURL,
  getGitLabLinkStatus,
  getGitLabLinkURL,
  type CreateRemoteRepoPayload,
  type GitHubRepo,
  type GitLabRepo,
} from '../../lib/api'

interface RepositoryAttachModalProps {
  appName: string
  onClose: () => void
  onAttachGitHub: (repo: GitHubRepo, branch: string) => void
  onAttachGitLab: (repo: GitLabRepo, branch: string) => void
  onCreateRemote: (payload: CreateRemoteRepoPayload) => void
  submitting?: boolean
  initialProvider?: 'github' | 'gitlab'
  initialMode?: 'create' | 'existing'
}

function defaultRepoName(appName: string): string {
  return appName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100) || 'cc-app'
}

export default function RepositoryAttachModal({
  appName,
  onClose,
  onAttachGitHub,
  onAttachGitLab,
  onCreateRemote,
  submitting = false,
  initialProvider = 'github',
  initialMode = 'existing',
}: RepositoryAttachModalProps) {
  const [provider, setProvider] = useState<'github' | 'gitlab'>(initialProvider)
  const [mode, setMode] = useState<'create' | 'existing'>(initialMode)
  const [repoName, setRepoName] = useState(defaultRepoName(appName))
  const [owner, setOwner] = useState('')
  const [description, setDescription] = useState(`Created from Code Concierge app ${appName}`)
  const [isPrivate, setIsPrivate] = useState(true)
  const [defaultBranch, setDefaultBranch] = useState('main')
  const [connectionLoading, setConnectionLoading] = useState(true)
  const [connected, setConnected] = useState({ github: false, gitlab: false })
  const [needsReconnect, setNeedsReconnect] = useState({ github: false, gitlab: false })

  useEffect(() => {
    let cancelled = false

    setConnectionLoading(true)
    Promise.allSettled([
      getGitHubLinkStatus(),
      getGitLabLinkStatus(),
    ]).then(([githubResult, gitlabResult]) => {
      if (cancelled) return
      setConnected({
        github: githubResult.status === 'fulfilled' ? githubResult.value.linked : false,
        gitlab: gitlabResult.status === 'fulfilled' ? gitlabResult.value.linked : false,
      })
    }).finally(() => {
      if (!cancelled) setConnectionLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [])

  function handleCreate(e: FormEvent) {
    e.preventDefault()
    const name = repoName.trim()
    if (!name || submitting) return
    onCreateRemote({
      provider,
      name,
      owner: owner.trim() || undefined,
      description: description.trim() || undefined,
      private: isPrivate,
      default_branch: defaultBranch.trim() || 'main',
    })
  }

  const providerConnected = connected[provider] && !needsReconnect[provider]
  const providerLabel = provider === 'gitlab' ? 'GitLab' : 'GitHub'
  const ProviderIcon = provider === 'gitlab' ? Gitlab : Github
  const connectUrl = provider === 'gitlab' ? getGitLabLinkURL() : getGitHubLinkURL()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card">
        <div className="flex items-start justify-between gap-4 border-b border-border p-6">
          <div>
            <h2 className="text-xl font-semibold">
              {!connectionLoading && !providerConnected ? `Connect ${providerLabel}` : 'Link Repository'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {!connectionLoading && !providerConnected
                ? `Connect ${providerLabel} before choosing a repository for this app.`
                : 'Create a new remote from this app, or attach an existing GitHub/GitLab repository.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
            aria-label="Close repository attach dialog"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-5 flex gap-2">
            <button
              type="button"
              onClick={() => setProvider('github')}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                provider === 'github'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Github className="w-4 h-4" />
              GitHub
            </button>
            <button
              type="button"
              onClick={() => setProvider('gitlab')}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                provider === 'gitlab'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Gitlab className="w-4 h-4" />
              GitLab
            </button>
          </div>

          {connectionLoading ? (
            <div className="py-8 text-center space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">Checking {providerLabel} connection...</p>
            </div>
          ) : !providerConnected ? (
            <div className="rounded-lg border border-border bg-secondary/20 p-6 text-center">
              <ProviderIcon className="mx-auto h-10 w-10 text-muted-foreground" />
              <h3 className="mt-4 text-base font-semibold">Connect {providerLabel}</h3>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                Connect {providerLabel} before choosing or creating a repository.
              </p>
              <div className="mt-5 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-lg border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <a
                  href={connectUrl}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <ProviderIcon className="h-4 w-4" />
                  Connect {providerLabel}
                </a>
              </div>
            </div>
          ) : (
            <>
          <div className="mb-5 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setMode('create')}
              className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                mode === 'create'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-secondary/20 hover:bg-secondary'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>
                <span className="block font-medium">Create new remote</span>
                <span className="block text-xs text-muted-foreground">Create repo, push this app, then link it.</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMode('existing')}
              className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                mode === 'existing'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-secondary/20 hover:bg-secondary'
              }`}
            >
              <Search className="w-4 h-4" />
              <span>
                <span className="block font-medium">Link existing repo</span>
                <span className="block text-xs text-muted-foreground">Choose a repo that already exists.</span>
              </span>
            </button>
          </div>

          {mode === 'create' ? (
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="rounded-lg border border-border bg-secondary/20 p-4">
                <p className="text-sm font-medium">Create {provider === 'gitlab' ? 'GitLab project' : 'GitHub repository'}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  The current app git history will be pushed to the new remote on the default branch. The stored
                  <span className="font-mono"> origin </span>
                  remote will not include access tokens.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Repository name</label>
                  <input
                    value={repoName}
                    onChange={(e) => setRepoName(e.target.value)}
                    disabled={submitting}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    placeholder={defaultRepoName(appName)}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {provider === 'gitlab' ? 'Namespace' : 'Owner / org'} <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <input
                    value={owner}
                    onChange={(e) => setOwner(e.target.value)}
                    disabled={submitting}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    placeholder={provider === 'gitlab' ? 'group/subgroup' : 'github-org'}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Default branch</label>
                  <input
                    value={defaultBranch}
                    onChange={(e) => setDefaultBranch(e.target.value)}
                    disabled={submitting}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    placeholder="main"
                    required
                  />
                </div>
                <label className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    disabled={submitting}
                    className="h-4 w-4"
                  />
                  Create as private
                </label>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-lg border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !repoName.trim()}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {submitting ? 'Creating…' : 'Create Remote & Link'}
                </button>
              </div>
            </form>
          ) : provider === 'github' ? (
            <GitHubRepoBrowser
              mode="attach"
              importing={submitting}
              actionLabel="Link Repository"
              onCancel={onClose}
              onNeedsReconnect={() => setNeedsReconnect((current) => ({ ...current, github: true }))}
              onSelect={(repo, branch) => onAttachGitHub(repo, branch)}
            />
          ) : (
            <GitLabRepoBrowser
              mode="attach"
              importing={submitting}
              actionLabel="Link Repository"
              onCancel={onClose}
              onNeedsReconnect={() => setNeedsReconnect((current) => ({ ...current, gitlab: true }))}
              onSelect={(repo, branch) => onAttachGitLab(repo, branch)}
            />
          )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
